import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
} from '@angular/router';
import { RecordList } from '@app/models/record.model';
import { ApiService } from '@app/services/api.service';
import { NoteBlobService } from '@app/services/noteblob.service';
import { WebviewService } from '@app/services/webview.service';
import { OS } from '@app/shared/enum/date';
import { RecordSelectors } from '@app/store/record/record.selectors';
import { SettingSelectors } from '@app/store/setting/setting.selectors';
import { Store } from '@ngxs/store';
import { EMPTY, forkJoin, mergeMap, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RecordDetailResolverService implements Resolve<RecordList> {
  constructor(
    private store: Store,
    private apiService: ApiService,
    private router: Router,
    private webViewService: WebviewService,
    private noteblobService: NoteBlobService,
  ) { }
  resolve(
    route: ActivatedRouteSnapshot,
  ): Observable<RecordList> {
    const id = route.paramMap.get('noteid') ?? '';
    const noteid = Number(id);
    const objkbn = route.paramMap.get('objkbn') ?? '';
    const objcd = route.paramMap.get('objcd') ?? '';

    const pageList = this.store.selectSnapshot(RecordSelectors.recordList) ?? [];
    const current = pageList.find((item) => {
      const { note } = item;
      return (
        note.objkbn === objkbn && note.objcd === objcd && note.noteid === noteid
      );
    });

    if (current) return of(current);

    return this.apiService.getNoteList([{ objkbn, objcd, noteid }]).pipe(
      switchMap((notes) => {
        if (notes.length > 0) {
          const updatedNotes$ = this.noteblobService.fetchAndMapNoteBlobs(notes);
          // Wait for all note updates to complete
          return forkJoin(updatedNotes$);
        }
        return of(notes);
      }),
      mergeMap((list) => {
        if (list && list.length > 0) {
          const note = list[0];
          return of({ note, displayOption: note.displayOption });
        }
        const previous = this.router
          .getCurrentNavigation()
          ?.previousNavigation?.finalUrl?.toString();
        if (!previous) {
          let isWebView = this.store.selectSnapshot(SettingSelectors.isWebView);
          let isAndroid = this.webViewService.getMobileOS() === OS.ANDROID;
          this.router.navigate(['/404'], { skipLocationChange: !(isWebView && !isAndroid) });
        }
        return EMPTY;
      })
    );
  }
}
